import { useState } from "react";
import { Button, ErrorText, Panel, SelectField, Spinner } from "../../components/ui";
import { fmtAmount, fmtDate } from "../../lib/format";
import { useOpportunities } from "../opportunities/hooks";
import { downloadInterestsCsv } from "./api";
import { useFunnel, useInterestActivities, useInterests, useUpdateInterest } from "./hooks";

const STATUSES = ["new", "contacted", "qualified", "converted", "rejected"] as const;

export function ProspectsPage() {
  const [status, setStatus] = useState("");
  const [opportunityId, setOpportunityId] = useState("");
  const [openRow, setOpenRow] = useState<string | null>(null);

  const funnel = useFunnel();
  const opps = useOpportunities();
  const interests = useInterests({
    status: status || undefined,
    opportunityId: opportunityId || undefined,
  });
  const update = useUpdateInterest();

  return (
    <Panel
      title="Prospects"
      actions={<Button variant="ghost" onClick={() => void downloadInterestsCsv()}>Exporter CSV</Button>}
    >
      {/* funnel */}
      <div className="funnel">
        {funnel.data && (
          <>
            <span className="funnel-total">{funnel.data.total} total</span>
            {STATUSES.map((s) => (
              <span key={s} className={`tag tag-${s}`}>
                {s} · {funnel.data!.byStatus[s] ?? 0}
              </span>
            ))}
          </>
        )}
      </div>

      {/* filters */}
      <div className="inline-form">
        <SelectField label="Statut" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">tous</option>
          {STATUSES.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </SelectField>
        <SelectField
          label="Opportunité"
          value={opportunityId}
          onChange={(e) => setOpportunityId(e.target.value)}
        >
          <option value="">toutes</option>
          {opps.data?.map((o) => (
            <option key={o.id} value={o.id}>
              {o.title}
            </option>
          ))}
        </SelectField>
      </div>

      <ErrorText error={interests.error} />
      {interests.isLoading ? (
        <Spinner />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Opportunité</th>
                <th>Email</th>
                <th>Montant</th>
                <th>Statut</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {interests.data?.map((i) => (
                <RowGroup
                  key={i.id}
                  interest={i}
                  open={openRow === i.id}
                  onToggle={() => setOpenRow(openRow === i.id ? null : i.id)}
                  onStatus={(s) => update.mutate({ id: i.id, patch: { status: s } })}
                  onNote={(internalNotes, comment) =>
                    update.mutate({ id: i.id, patch: { internalNotes, comment } })
                  }
                  saving={update.isPending}
                />
              ))}
              {interests.data?.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted">
                    Aucun prospect.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

function RowGroup({
  interest,
  open,
  onToggle,
  onStatus,
  onNote,
  saving,
}: {
  interest: import("./api").Interest;
  open: boolean;
  onToggle: () => void;
  onStatus: (s: string) => void;
  onNote: (notes: string | null, comment: string | null) => void;
  saving: boolean;
}) {
  const [notes, setNotes] = useState(interest.internalNotes ?? "");
  const [comment, setComment] = useState("");
  const activities = useInterestActivities(open ? interest.id : null);

  return (
    <>
      <tr>
        <td>{fmtDate(interest.createdAt)}</td>
        <td>{interest.opportunityTitle ?? "—"}</td>
        <td>{interest.email ?? interest.userEmail ?? "—"}</td>
        <td>{fmtAmount(interest.amountIntended)}</td>
        <td>
          <select value={interest.status} disabled={saving} onChange={(e) => onStatus(e.target.value)}>
            {STATUSES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </td>
        <td>
          <button className="btn btn-ghost" onClick={onToggle}>
            {open ? "Fermer" : "Détails"}
          </button>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={6}>
            <div className="prospect-detail">
              {interest.message && (
                <p>
                  <strong>Message :</strong> {interest.message}
                </p>
              )}
              <label className="field">
                <span>Notes internes</span>
                <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </label>
              <label className="field">
                <span>Commentaire (journalisé)</span>
                <input value={comment} onChange={(e) => setComment(e.target.value)} />
              </label>
              <Button
                disabled={saving}
                onClick={() => {
                  onNote(notes || null, comment || null);
                  setComment("");
                }}
              >
                Enregistrer
              </Button>

              <h4>Activité</h4>
              {activities.isLoading ? (
                <Spinner />
              ) : (
                <ul className="activity-log">
                  {activities.data?.map((a) => (
                    <li key={a.id}>
                      <span className="muted">{fmtDate(a.createdAt)}</span> — {a.action}
                      {a.fromStatus && ` (${a.fromStatus} → ${a.toStatus})`}
                      {a.comment && ` : ${a.comment}`}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
